import { DestroyRef, inject, Injectable } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { MetaState } from '@df/core/meta/meta-state';
import { TimeoutService } from '@df/ng/timeout.service';
import { BasketService, IAddToBasketSuccessEventData } from '@df/session/api/basket/basket.service';
import { CustomerModel } from '@df/session/api/customer.model';
import compact from 'lodash-es/compact';
import uniq from 'lodash-es/uniq';
import without from 'lodash-es/without';
import { IPioEvent } from './interfaces';
import { PioService } from './pio.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
@Injectable({
    providedIn: 'root'
})
export class PioTrackingService {
    get enabled() {
        return this.pioService.trackingEnabled;
    }

    /**
     * Takes tracking logic
     * direct : track simples only, skip configurables or bundles
     * parent : track parents only, skip simples
     * mixed  : track both
     */
    get logic(): 'direct' | 'parent' | 'mixed' {
        return this.app.data?.['pio_config']?.logic || 'direct';
    }

    /**
     * Events tracked awaiting to be setn
     * @type {IPioEvent[]}
     */
    protected trackLayer: IPioEvent[] = [];

    protected last: { [key: number]: number } = {};

    productViewDebounced!: (productId: number) => void;

    protected requestService = inject(RequestService);
    protected meta = inject(Meta);
    protected customer = inject(CustomerModel);
    protected app = inject(App);
    protected productResource = inject(ProductResource);
    protected pioService = inject(PioService);
    protected basketService = inject(BasketService);
    protected timeoutService = inject(TimeoutService);
    protected destroyRef = inject(DestroyRef);

    appInitialize() {
        this.productViewDebounced = this.timeoutService.debounce((productId: number) => this.productView(productId), 1000);

        if (this.enabled) {
            this.meta.subject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((metaState?: MetaState) => {
                if (metaState?.model && metaState.model.type === Product.type) {
                    this.productViewDebounced(<number>metaState.model.id);
                }
            });

            this.customer.obs.status.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isLoggedIn => {
                isLoggedIn ? this.pioService.sendUidRequest() : this.pioService.resetUid();
            });

            this.customer.obs.email$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(email => {
                if (email) {
                    this.pioService.sendUidRequest();
                }
            });

            // on basket.add.success event send addToBasked tracking data
            this.basketService.addToBasketSuccessSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: IAddToBasketSuccessEventData) => this.onAddToBasket(data));

            // on checkout.success.new-order event send purchase tracking data
            this.app.events.on(Checkout.EVENTS.SuccessTracking).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: Checkout.ISuccessData) => this.onNewOrder(data));
        }
    }

    /**
     * Normalizes ids to track according to engine
     *
     * @param  {number}   productId  Number
     * @param  {string}   actionName String If given we will skip passing same parent twice (use with view and preview events)
     * @return {number[]}            Ids to track
     */
    protected normalizeProductId(productId: number, actionName?: string): { ids: number[]; parent?: number } {
        const model = <Product<IProductDataV2>>this.productResource.get(productId);
        let ids: number[] = [];
        let parent: number | undefined;
        if (model) {
            // direct or mixed logic
            if (this.logic !== 'parent') {
                // when viewing / buysing / .. simple push directly
                if (model.isSimple) {
                    ids.push(model.id);
                }
                // when viewing / buying / .. bundle, simples are never exposed, we need to push them manually
                else if (model.isBundle) {
                    ids.push(...model.childrenIds);
                }
                // when viewing / buying / .. bundle, simples are never exposed, we need to push them manually
                else if (model.isGrouped) {
                    ids.push(...model.childrenIds);
                }
            }
            // parent or mixed logic
            if (this.logic !== 'direct') {
                // for parent-type products we push them directly
                if (model.isConfigurable || model.isBundle || model.isGrouped) {
                    parent = model.id;
                    ids.push(parent);
                }
                // for simple products we need to push their parents
                else if (model.isSimple && model.data) {
                    // if product has a parent then add it, and record as last parent
                    if (model.data.parent) {
                        parent = model.data.parent;
                        ids.push(parent);
                    }
                    // if its simple without parent we need to push it to, but without parent-prevent logic
                    else {
                        ids.push(model.id);
                    }
                }
            }
        }

        // make it unique
        ids = uniq(ids);

        // if we use prevention not to track same parent twice in row, then we need to check it
        if (actionName) {
            if (parent && this.last[actionName] === parent) {
                ids = without(ids, parent);
            } else {
                this.last[actionName] = parent;
                // skip parent - its not been changed, we dont want to trigger configuration event
                parent = undefined;
            }
        }

        return { ids, parent };
    }
    /**
     * Track product view (on product page)
     */

    public productView = (productId: number): void => {
        const track = this.normalizeProductId(productId, 'view');

        this.track('view', track.ids);
        this.track('view-configure', track.parent);
    };

    /**
     * Track product preview (popup on listing)
     */
    private _productPreview(productId: number): void {
        const track = this.normalizeProductId(productId, 'preview');
        this.track('preview', track.ids);
        this.track('preview-configure', track.parent);
    }

    /**
     * Track product added to basket
     */
    public addToBasket(productId: number): void {
        this.track('basket-add', this.normalizeProductId(productId).ids);
    }

    /**
     * Track product added to wishlist
     */
    public addToWishlist(productId: number): void {
        this.track('wishlist-add', this.normalizeProductId(productId).ids);
    }

    /**
     * Track product sold
     */
    public purchase(productId: number): void {
        this.track('purchase', this.normalizeProductId(productId).ids);
    }

    /**
     * Adds tracking event and sends it00
     * @param {string}    eventName [description]
     * @param {number |         number[]}    productIds [description]
     */
    public track(eventName: string, productIds?: number | number[]): void {
        // do nothing for prerender or when disallowed
        if (!this.enabled || !productIds) {
            return;
        }

        // do nothing if disabled

        const date = new Date();
        const eventTime = date.toISOString();

        const ids: number[] = Array.isArray(productIds) ? productIds : [productIds];

        ids.forEach(id => {
            this.trackLayer.push({
                targetEntityId: this.pioService.toItemId(id),
                event: eventName,
                entityType: 'user',
                // entityId : <- we inject it later see send track
                targetEntityType: 'item',
                eventTime
            });
        });

        this._sendEvents();
    }

    /**
     * Sends all tracking requests
     * Its written this way cause we may not have userId
     * when we want to track events. So we buffer them in _trackLayer
     * and process when user id is known.
     */
    protected _sendEvents(): void {
        this.pioService.getUserId(userId => {
            if (userId) {
                let event: IPioEvent | undefined;
                // tslint:disable-next-line: no-conditional-assignment
                while ((event = this.trackLayer.shift())) {
                    event.entityId = userId;
                    this.requestService.send(undefined, { path: 'piotrack', data: event }, undefined, undefined, { api: 'node' });
                }
            }
        });
    }

    onAddToBasket(data: IAddToBasketSuccessEventData) {
        const products = data.request.products;
        if (products) {
            products.forEach(product => this.addToBasket(product.id));
        }
    }

    onNewOrder(data: Checkout.ISuccessData) {
        const ids = data.items && (compact(data.items.map(i => i.product_id)) as number[]);
        if (ids && ids.length) {
            this.productResource.loadAllIds(ids, 3).then(() => ids.forEach(id => this.purchase(id)));
        }
    }
}
